/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import {
  FirewallPolicy,
  FirewallPolicy$Outbound,
  FirewallPolicy$outboundSchema,
} from "./firewallpolicy.js";
import {
  LoggingPolicy,
  LoggingPolicy$Outbound,
  LoggingPolicy$outboundSchema,
} from "./loggingpolicy.js";
import {
  MatchExpr,
  MatchExpr$Outbound,
  MatchExpr$outboundSchema,
} from "./matchexpr.js";

/**
 * Partial update of a single policy. Omitted fields keep their stored
 *
 * @remarks
 * values; at least one updatable field must be provided. Providing one of
 * `firewall` or `logging` replaces the
 * policy's rule entirely, including switching its type; at most one may be
 * set.
 */
export type V1GatewayUpdatePolicyRequestBody = {
  /**
   * Identifies a resource by either its unique ID or its slug.
   *
   * @remarks
   * Accepts a prefixed ID (such as 'proj_' or 'app_') or a slug.
   */
  project: string;
  /**
   * Identifies a resource by either its unique ID or its slug.
   *
   * @remarks
   * Accepts a prefixed ID (such as 'proj_' or 'app_') or a slug.
   */
  app: string;
  /**
   * Identifies a resource by either its unique ID or its slug.
   *
   * @remarks
   * Accepts a prefixed ID (such as 'proj_' or 'app_') or a slug.
   */
  environment: string;
  /**
   * Id of the policy to update, as returned by `gateway.listPolicies`.
   *
   * @remarks
   * Ids are regenerated whenever `gateway.setPolicies` replaces the list,
   * so list the policies first if you are unsure the id is current.
   */
  policyId: string;
  /**
   * New human-readable name. Omit to keep the current name.
   */
  name?: string | undefined;
  /**
   * Enable or disable the policy. Disabled policies are stored but skipped
   *
   * @remarks
   * during evaluation. Omit to keep the current setting.
   */
  enabled?: boolean | undefined;
  /**
   * Replaces all match expressions. Set null to remove them so the policy
   *
   * @remarks
   * applies to every request. Omit to keep the current expressions.
   */
  match?: Array<MatchExpr> | null | undefined;
  /**
   * Blocks matching requests.
   */
  firewall?: FirewallPolicy | undefined;
  /**
   * Adds request data to the log entries of matching requests. The gateway
   *
   * @remarks
   * always records a basic log entry for every request: method, host, path,
   * status, and latency. Each capture setting is a separate opt-in: request
   * headers, response headers, request body, response body, and query data.
   * The policy's `match` expressions select the requests. A policy without
   * `match` expressions matches every request. If more than one enabled
   * logging policy matches a request, the gateway combines their settings.
   * The gateway always redacts the `Authorization` header and configured key
   * locations before it stores headers or query data.
   */
  logging?: LoggingPolicy | undefined;
};

/** @internal */
export type V1GatewayUpdatePolicyRequestBody$Outbound = {
  project: string;
  app: string;
  environment: string;
  policyId: string;
  name?: string | undefined;
  enabled?: boolean | undefined;
  match?: Array<MatchExpr$Outbound> | null | undefined;
  firewall?: FirewallPolicy$Outbound | undefined;
  logging?: LoggingPolicy$Outbound | undefined;
};

/** @internal */
export const V1GatewayUpdatePolicyRequestBody$outboundSchema: z.ZodType<
  V1GatewayUpdatePolicyRequestBody$Outbound,
  z.ZodTypeDef,
  V1GatewayUpdatePolicyRequestBody
> = z.object({
  project: z.string(),
  app: z.string(),
  environment: z.string(),
  policyId: z.string(),
  name: z.string().optional(),
  enabled: z.boolean().optional(),
  match: z.nullable(z.array(MatchExpr$outboundSchema)).optional(),
  firewall: FirewallPolicy$outboundSchema.optional(),
  logging: LoggingPolicy$outboundSchema.optional(),
});

export function v1GatewayUpdatePolicyRequestBodyToJSON(
  v1GatewayUpdatePolicyRequestBody: V1GatewayUpdatePolicyRequestBody,
): string {
  return JSON.stringify(
    V1GatewayUpdatePolicyRequestBody$outboundSchema.parse(
      v1GatewayUpdatePolicyRequestBody,
    ),
  );
}
